import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFAQs, createFAQ, updateFAQ, deleteFAQ, reset } from '../../features/faqs/faqSlice';
import { useToast } from '../../contexts/ToastContext';
import Modal from '../../components/Modal';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const ManajemenFAQ = () => {
  const dispatch = useDispatch();
  const { addToast } = useToast();
  const { faqs, isLoading, isSuccess, isError, message } = useSelector((state) => state.faqs);

  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    order: 0
  });
  const [editingId, setEditingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    dispatch(getFAQs());
  }, [dispatch]);

  useEffect(() => {
    if (isSuccess) {
      addToast({ type: 'success', message: editingId ? 'FAQ berhasil diperbarui!' : 'FAQ berhasil ditambahkan!' });
      resetForm();
      dispatch(reset());
    }
    if (isError) {
      addToast({ type: 'error', message: message || 'Terjadi kesalahan!' });
      dispatch(reset());
    }
  }, [isSuccess, isError, message, dispatch, addToast, editingId]);

  const resetForm = () => {
    setFormData({ question: '', answer: '', order: 0 });
    setEditingId(null);
    setIsModalOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingId) {
      dispatch(updateFAQ({ id: editingId, faqData: formData }));
    } else {
      dispatch(createFAQ(formData));
    }
  };

  const handleEdit = (faq) => {
    setEditingId(faq._id);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      order: faq.order
    });
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Yakin ingin menghapus FAQ ini?')) {
      dispatch(deleteFAQ(id)).unwrap().then(() => {
        addToast({ type: 'success', message: 'FAQ berhasil dihapus!' });
      });
    }
  };

  const handleAnswerChange = (value) => {
    setFormData((prev) => ({ ...prev, answer: value }));
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Manajemen FAQ</h1>
        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="flex items-center gap-2 px-6 py-3 bg-dark-blue-700 text-white rounded-xl font-medium hover:bg-dark-blue-800 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Tambah FAQ
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Pertanyaan</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Urutan</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {faqs.map((faq) => (
              <tr key={faq._id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm text-gray-900 font-medium">{faq.question}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{faq.order}</td>
                <td className="px-6 py-4 text-sm flex gap-3">
                  <button
                    onClick={() => handleEdit(faq)}
                    className="text-dark-blue-600 hover:text-dark-blue-800 font-medium"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(faq._id)}
                    className="text-red-600 hover:text-red-800 font-medium"
                  >
                    Hapus
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={resetForm}
        title={editingId ? 'Edit FAQ' : 'Tambah FAQ'}
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Pertanyaan</label>
            <input
              type="text"
              value={formData.question}
              onChange={(e) => setFormData((prev) => ({ ...prev, question: e.target.value }))}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-dark-blue-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Jawaban</label>
            <ReactQuill
              theme="snow"
              value={formData.answer}
              onChange={handleAnswerChange}
              className="bg-gray-50 rounded-xl"
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                  [{ 'color': [] }, { 'background': [] }],
                  ['link', 'blockquote'],
                  ['clean']
                ]
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Urutan</label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData((prev) => ({ ...prev, order: Number(e.target.value) }))}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-dark-blue-500 focus:border-transparent"
              required
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={resetForm}
              className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-6 py-3 bg-dark-blue-700 text-white rounded-xl font-medium hover:bg-dark-blue-800 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Menyimpan...' : editingId ? 'Update' : 'Simpan'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ManajemenFAQ;
